import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { DxFormComponent } from 'devextreme-angular';
import { LoadingService } from '../../services/loading.service';
import { TrainingAnonymousService } from '../../services/training-anonymous.service';
import { TrainingAnonymousModel } from '../../models/training/training-anonymous.model';
import { HttpStatusCode } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { EventParticipationQuestionModel } from 'src/app/models/event-participations/event-participation-question.model';
import { AnswerTrainingQuestionsDialogComponent } from '../answer-training-questions-dialog/answer-training-questions-dialog.component';
import { TrainingAnonymousStatusModel } from 'src/app/models/training/training-anonymous-status.model';
import { AcceptTrainingAsGuestModel } from 'src/app/models/training/accept-training-anonymous.model';
import { RegistrationFieldConfigModel } from 'src/app/models/registration-field-configs/registration-field-config.model';
import { REGISTRATION_FIELD_LABEL_KEYS, REGISTRATION_FIELD_REQUIRED_MESSAGE_KEYS } from 'src/app/config/registration-field-config/registration-field.text-maps';
import { REGISTRATION_FIELD_KEYS } from 'src/app/config/registration-field-config/registration-field.keys';
import { GuestRegistrationStorageService } from 'src/app/services/guest-registration-storage.service';

@Component({
  selector: 'app-accept-invitation-modal',
  templateUrl: './accept-invitation-modal.component.html',
  styleUrl: './accept-invitation-modal.component.scss',
  standalone: false
})
export class AcceptInvitationModalComponent implements OnInit {
  @ViewChild(AnswerTrainingQuestionsDialogComponent, { static: false })
  answerQuestionsDialogComponent?: AnswerTrainingQuestionsDialogComponent;

  @Input() showModal = false;
  @Input() training: TrainingAnonymousModel | null = null;
  @Input() trainingKey = '';
  @Input() channelToken: string | null = null;
  @Input() questions: EventParticipationQuestionModel[] | null = null;
  @Input() remainingParticipantSlots: number | null = null;
  @Input() registrationFieldConfigs: RegistrationFieldConfigModel[] = [];

  @Input() accessKey: string | null = null;
  @Output() accessKeyChange = new EventEmitter<string | null>();

  @Output() showModalChange = new EventEmitter<boolean>();
  @Output() onInviteAccepted = new EventEmitter();
  @Output() onAddedToQueue = new EventEmitter();
  @Output() onRequestSent = new EventEmitter();

  showTrainingWaitingListModal: boolean = false;

  isTermsAccepted: boolean | null | undefined = false;
  isShowForm: boolean = false;
  isFormSubmitted: boolean = false;
  isRequestFormSubmitted: boolean = false;
  isAddedToQueue: boolean = false;
  personNotFound: boolean = false;
  formData = {
    email: '',
    firstName: '',
    lastName: '',
    agentNumber: null,
    iddNumber: null,
    channelToken: null,
    answers: null
  } as AcceptTrainingAsGuestModel;

  @ViewChild('form') form!: DxFormComponent;

  readonly emailKey = REGISTRATION_FIELD_KEYS.email;

  get popupHeight(): string| number {
    const showQuestions =
      this.isShowForm &&
      !this.isFormSubmitted &&
      !this.isRequestFormSubmitted &&
      this.questions?.length;

    const FIELD_SIZE = 60;
    const BASE_SIZE = 220;

    const visibleFields = this.registrationFieldConfigs
      .filter(config => config.visible);
    const fieldCount = visibleFields.length;

    if (showQuestions) {
      return 'auto';
    }

    return fieldCount * FIELD_SIZE + BASE_SIZE;
  }

  constructor(
    private trainingAnonymousService: TrainingAnonymousService,
    private loadingService: LoadingService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private router: Router,
    private guestRegistrationStorageService: GuestRegistrationStorageService
  ) {}

  ngOnInit() {
    if(this.training?.guestsOnly) {
      this.isShowForm = true;
    }
  }

  ngAfterViewInit() {
    if(this.channelToken) {
      this.formData.channelToken = this.channelToken;
    }
  }

  public getLabelKey(key: string): string {
    return REGISTRATION_FIELD_LABEL_KEYS[key] ?? key;
  }

  public getRequiredMessageKey(key: string): string {
    return REGISTRATION_FIELD_REQUIRED_MESSAGE_KEYS[key] ?? key;
  }

  isFormValid(): boolean {
    let isValid = true;

    const result = this.form.instance.validate();
    if (!result.isValid) {
      this.scrollToFirstBrokenRule(result)
      isValid = false;
    }

    if(this.answerQuestionsDialogComponent){
      const areQuestionsValid = this.answerQuestionsDialogComponent.validate();
      if(!areQuestionsValid) isValid = false;
    }

    return isValid;
  }
  
  addToQueue() {
    const isValid = this.isFormValid();

    if (!isValid) {
      return;
    }

    if(this.answerQuestionsDialogComponent){
      this.formData.answers = this.answerQuestionsDialogComponent.answers;
    }

    this.showTrainingWaitingListModal = true;
  }

  onAddToQueue() {
    if(this.answerQuestionsDialogComponent){
      this.formData.answers = this.answerQuestionsDialogComponent.answers;
    }

    this.loadingService.load(
      this.trainingAnonymousService.enqueueTrainingAsGuest(
        this.trainingKey,
        this.formData
      )
    ).subscribe({
       next: (key) => {
          if (key) {
            this.guestRegistrationStorageService.save(this.formData);

            this.accessKey = key;
            this.accessKeyChange.emit(this.accessKey);
          }
          this.toastrService.success(
            this.translateService.instant('TRAINING-WAITING-LIST.ADDED-TO-QUEUE-SUCCESSFULLY')
          );

          this.isAddedToQueue = true;
          this.isFormSubmitted = true;
          this.showTrainingWaitingListModal = false;
          this.onAddedToQueue.emit();
        },
        error: (error) => {
          const code = this.parseApiError(error.error)?.code;

          if (error.status === HttpStatusCode.Conflict) {

            if (code === 'PERSON_NOT_FOUND') {
              this.personNotFound = true;
              this.isFormSubmitted = true;
              return;
            }
          }
          else if (error.status === HttpStatusCode.Unauthorized) {

            if (code === 'GUEST_NOT_ALLOWED_FOR_REGISTERED_USER') {
              this.navigateTrainingWaitList();
              return;
            }

          } else {
            this.toastrService.error(
              this.translateService.instant("COMMON.AN-ERROR-HAS-OCCURRED")
            );
          }
        },
    });
  }

  onAddToQueueClose() {
    this.showTrainingWaitingListModal = false;
  }

  acceptInvitation() {
    const isValid = this.isFormValid();

    if (!isValid) {
      return;
    }

    if(this.answerQuestionsDialogComponent){
      this.formData.answers = this.answerQuestionsDialogComponent.answers;
    }

    this.loadingService
      .load(
        this.trainingAnonymousService.acceptTrainingAsGuest(
          this.trainingKey,
          this.formData
        )
      )
      .subscribe({
        next: (key) => {
          if (key) {
            this.accessKey = key;
            this.accessKeyChange.emit(this.accessKey);
          }
          this.toastrService.success('Invitation accepted successfully');
          this.guestRegistrationStorageService.save(this.formData);

          this.onInviteAccepted.emit();
          this.isFormSubmitted = true;
        },
        error: (error) => {
          const code = this.parseApiError(error.error)?.code;

          if (error.status === HttpStatusCode.Conflict) {

            if (code === 'TRAINING_FULL') {
              this.training!.status = TrainingAnonymousStatusModel.BookedUp;
              this.showTrainingWaitingListModal = true;
              return;
            }

            if (code === 'PERSON_NOT_FOUND') {
              this.personNotFound = true;
              this.isFormSubmitted = true;
              return;
            }
          }
          else if (error.status === HttpStatusCode.Unauthorized) {

            if (code === 'GUEST_NOT_ALLOWED_FOR_REGISTERED_USER') {
              this.navigateTraining();
              return;
            }

          } else {
            this.toastrService.error(
              this.translateService.instant("COMMON.AN-ERROR-HAS-OCCURRED")
            );
          }
        },
      });
  }

  private parseApiError(error: any): any {
    if (typeof error === 'string') {
      try {
        return JSON.parse(error);
      } catch {
        return {};
      }
    }
    return error;
  }

  private scrollToFirstBrokenRule(result: any): void {
    if (!result?.brokenRules?.length) return;

    const firstBrokenRule = result.brokenRules[0] as any;
    const element = firstBrokenRule?.validator?.$element?.();

    element?.[0]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    element?.[0]?.focus();
  }

  navigateTraining() {
    window.location.href = window.location.origin + `/my-trainings/training/${this.training!.id}?autoaccept=true`
  }

  navigateTrainingWaitList() {
    window.location.href = window.location.origin + `/my-trainings/training/${this.training!.id}?auto-waitlist=true`
  }

  showForm() {
    this.isShowForm = true;

    this.loadSavedGuestDetails();
  }

  private loadSavedGuestDetails(): void {
    const savedDetails = 
      this.guestRegistrationStorageService.get();

    if (!savedDetails) {
      return;
    }

    this.registrationFieldConfigs
      .filter(field => field.visible)
      .forEach(field => {
        if (field.key in savedDetails) {
          (this.formData as any)[field.key] = 
            (savedDetails as any)[field.key];
        }
      });
  }

  closeModal() {
    this.showModal = false;
    this.showModalChange.emit(this.showModal);

    if (this.accessKey) {
      this.router.navigate([`/my-invitation/${this.training!.key}/${this.accessKey}`]);
    }
  }

  cancelError() {
    this.personNotFound = false;
    this.isFormSubmitted = false;
  }

  sendRequest() {
    this.loadingService
      .load(
        this.trainingAnonymousService.requestTrainingAnonymous(
          this.trainingKey,
          this.formData
        )
      )
      .subscribe({
        next: () => {
          this.toastrService.success('Request sent');
           this.guestRegistrationStorageService.save(this.formData);

          this.isFormSubmitted = false;
          this.isRequestFormSubmitted = true;
          this.onRequestSent.emit();
        }
      });
  }

  onPopupHiding() {
    console.log('On popup hide event!');
  }

  toggleCheckbox(checkbox: any) {
    checkbox.instance.option('value', !this.isTermsAccepted);
  }

  onBackClick() {
    this.isShowForm = false;
  }
}
